/* FORGE set modules — the add path for BOTH set surfaces.
   Replaces assets/forge-set.js (PDP "Bought together") and assets/forge-sets.js (homepage
   "Matched sets" rail). One file, because after 2026-08-16 the two surfaces share real
   behaviour — size resolution, in-cart de-duplication, live totals — and two copies of that
   would be two copies to drift apart. The surface tag that used to be the reason for two
   files is now read off the container's data-surface.

   IT IS STILL PURELY ADDITIVE. Every row can add itself with this file absent: a
   single-variant row is a Dawn <product-form>, a multi-variant row is a real <form> whose
   size chips are `type="submit" name="id"` buttons inside a native <details>. That is why
   the add-all button ships `hidden` in the Liquid and is revealed here — a control that
   cannot work is never shown.

   WHAT THIS FILE IS RESPONSIBLE FOR
     1. Adding a row's CHOSEN size without leaving the page (intercepting the row form's
        submit and reading evt.submitter, which is the chip that was pressed).
     2. The add-all: resolving every member's variant first, asking for any size that is
        still missing, then posting one /cart/add.js call with an `items` array — Dawn's
        <product-form> can only post one line, which is why this goes to the Cart API
        directly and hands the returned sections to the drawer exactly as
        assets/product-form.js does. Same contract, same repaint.
     3. Keeping the button honest: it re-counts and re-totals from the rows on every size
        pick, every buy-box variant change and every cart update, and skips any variant
        already in the cart so nothing is ever added twice.

   TWO RULES THIS FILE WILL NOT BREAK
     - It never invents a variant id. A multi-variant row arrives with none; the only thing
       that can give it one is a chip press.
     - It never adds a total it did not just display. If resolving the last missing size
       moves the total (the wrist wraps are $25/$30/$38 by length), it re-labels the button
       and waits for a second press instead of charging a number that was never on screen. */
(function () {
  'use strict';

  var sets = document.querySelectorAll('[data-forge-set]');
  if (!sets.length) {
    return;
  }

  var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  /* ---- variant ids currently in the cart -------------------------------------------------
     Shared by every set on the page and refreshed after any add, ours or the framework's.
     `null` means "not known yet", which is treated as "nothing is in the cart" so a slow
     /cart.js can never block or mislabel a button. */
  var inCart = null;
  var cartListeners = [];

  function refreshCart() {
    return fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var ids = {};
        (data.items || []).forEach(function (line) { ids[String(line.variant_id)] = true; });
        inCart = ids;
      })
      .catch(function () { /* leave the last known state; a stale label beats a broken one */ })
      .then(function () {
        cartListeners.forEach(function (fn) { fn(); });
      });
  }

  function isInCart(id) {
    return !!(inCart && inCart[String(id)]);
  }

  /* ---- money ----------------------------------------------------------------------------
     The shop's own money_format, so a currency change in Shopify needs no code change here.
     Handles the four separator variants Shopify emits; anything else falls back to the
     plain amount rather than printing an empty string. */
  function formatCents(cents, decimals, thousands, decimalMark) {
    var n = Math.round(Math.abs(cents)) / 100;
    var fixed = n.toFixed(decimals);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.length > 1 ? whole + decimalMark + parts[1] : whole;
  }

  function money(cents, format) {
    var fmt = format || '${{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, token) {
      switch (token) {
        case 'amount': return formatCents(cents, 2, ',', '.');
        case 'amount_no_decimals': return formatCents(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return formatCents(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return formatCents(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return formatCents(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return formatCents(cents, 0, ' ', ',');
        case 'amount_with_space_separator': return formatCents(cents, 2, ' ', ',');
        default: return formatCents(cents, 2, ',', '.');
      }
    });
  }

  /* ---- one set module -------------------------------------------------------------------- */
  function initSet(root) {
    var surface = root.getAttribute('data-surface') || 'pdp-set';
    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var shipGate = parseInt(root.getAttribute('data-ship-gate'), 10) || 0;

    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-forge-row]'));
    var addAll = root.querySelector('[data-forge-addall]');
    var errEl = root.querySelector('[data-forge-error]');
    var hintEl = root.querySelector('[data-forge-hint]');
    var shipEl = root.querySelector('[data-forge-shipnote]');
    var busy = false;
    /* Set while the add-all is walking the shopper through the sizes it still needs. In that
       mode a chip RESOLVES its row instead of adding it on its own, because the shopper asked
       for the whole set, not for that one product. */
    var pending = false;
    var pendingTotal = 0;

    function say(el, message) {
      if (!el) { return; }
      if (message) {
        el.textContent = message;
        el.hidden = false;
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    }

    function rowPrice(row) {
      return parseInt(row.getAttribute('data-price'), 10) || 0;
    }

    /* A row that could still contribute a line: it has an addable variant (or a size to
       choose), and whatever it would add is not already in the cart. */
    function candidates() {
      return rows.filter(function (row) {
        var vid = row.getAttribute('data-vid');
        if (vid) { return !isInCart(vid); }
        return row.hasAttribute('data-multi') && !!row.querySelector('[data-forge-sizes] .sz:not([disabled])');
      });
    }

    function unresolved(list) {
      return list.filter(function (row) { return !row.getAttribute('data-vid'); });
    }

    function totalOf(list) {
      return list.reduce(function (sum, row) { return sum + rowPrice(row); }, 0);
    }

    function relabel() {
      if (!addAll) { return; }
      var list = candidates();
      if (list.length < 2) {
        /* One line left is not a set — the row's own control already does that job, and a
           "1 item" button beside it would just be a second way to press the same thing. */
        addAll.hidden = true;
        if (shipEl) { shipEl.hidden = true; }
        return;
      }
      var total = totalOf(list);
      addAll.hidden = false;
      addAll.textContent = 'Add ' + list.length + ' items — ' + money(total, moneyFormat);

      if (shipEl && shipGate > 0) {
        shipEl.hidden = false;
        if (total >= shipGate) {
          shipEl.textContent = 'Ships free';
          shipEl.classList.add('is-free');
        } else {
          shipEl.textContent = money(shipGate - total, moneyFormat) + ' away from free shipping';
          shipEl.classList.remove('is-free');
        }
      }
    }

    /* ---- adding ---------------------------------------------------------------------- */
    function lineFor(row) {
      return {
        id: parseInt(row.getAttribute('data-vid'), 10),
        quantity: 1,
        properties: {
          _upsell: surface,
          /* The viewed product is not an upsell — it is the thing the shopper came for. It
             still carries the surface tag (this surface really did add the line) but a
             distinct reason, so set revenue reporting can separate the anchor from the
             products this module actually sold. */
          _upsell_reason: row.hasAttribute('data-anchor') ? 'set-anchor' : 'set',
          _upsell_colour: row.getAttribute('data-upsell-colour') || 'match'
        }
      };
    }

    function post(items, control) {
      if (busy || !items.length) { return; }
      busy = true;
      if (control) {
        control.setAttribute('aria-disabled', 'true');
        control.classList.add('loading');
      }
      say(errEl, '');

      var body = { items: items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map(function (s) { return s.id; }).join(',');
        body.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') { cart.setActiveElement(document.activeElement); }
      }

      return fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          /* The Cart API returns a `status` field only on failure. */
          if (data && data.status) {
            say(errEl, data.description || data.message || 'Could not add these to your cart.');
            return;
          }
          if (!cart) { window.location = cartUrl; return; }
          if (cart.classList.contains('is-empty')) { cart.classList.remove('is-empty'); }
          cart.renderContents(data);
        })
        .catch(function () {
          say(errEl, 'Could not add these to your cart. Please try again.');
        })
        .then(function () {
          busy = false;
          if (control) {
            control.removeAttribute('aria-disabled');
            control.classList.remove('loading');
          }
          return refreshCart();
        });
    }

    /* ---- size disclosure ------------------------------------------------------------- */
    function openSizes(row, message) {
      var details = row.querySelector('[data-forge-sizes]');
      if (!details) { return; }
      details.open = true;
      var first = details.querySelector('.sz:not([disabled])');
      if (first) { first.focus(); }
      say(hintEl, message || '');
    }

    function markChosen(row, chip) {
      var details = row.querySelector('[data-forge-sizes]');
      if (details) {
        details.querySelectorAll('.sz').forEach(function (c) { c.classList.remove('on'); });
      }
      chip.classList.add('on');
      row.setAttribute('data-vid', chip.value);
      row.setAttribute('data-price', chip.getAttribute('data-price') || row.getAttribute('data-price'));
      var priceEl = row.querySelector('[data-forge-row-price]');
      if (priceEl && chip.getAttribute('data-money')) { priceEl.textContent = chip.getAttribute('data-money'); }
      var summary = details ? details.querySelector('summary .szpick') : null;
      if (summary) { summary.textContent = chip.getAttribute('data-title') || ''; }
    }

    rows.forEach(function (row) {
      var form = row.querySelector('form');
      var details = row.querySelector('[data-forge-sizes]');
      if (!form || !details) { return; }

      /* The row's control grows to the full row width while its sizes are on screen, so nine
         chips wrap under the row instead of squeezing the name column. :has() does this on
         its own in current browsers; the class is here so older ones behave identically. */
      details.addEventListener('toggle', function () {
        var ctl = row.querySelector('.ctl');
        if (ctl) { ctl.classList.toggle('is-open', details.open); }
        if (!details.open) { say(hintEl, ''); }
      });

      /* A summary the SHOPPER pressed ends any add-all walkthrough — they have gone back to
         driving one row at a time. */
      var summary = details.querySelector('summary');
      if (summary) {
        summary.addEventListener('click', function () { pending = false; say(hintEl, ''); });
      }

      form.addEventListener('submit', function (evt) {
        var chip = evt.submitter;
        if (!chip || chip.name !== 'id' || !chip.value) {
          /* No chip means no variant. Never guess one — open the picker instead. */
          evt.preventDefault();
          openSizes(row, 'Choose a ' + (details.getAttribute('data-label') || 'size') + ' first.');
          return;
        }
        evt.preventDefault();
        markChosen(row, chip);
        details.open = false;

        if (pending) {
          /* Mid add-all: resolve the next missing size, or finish the job. */
          var list = candidates();
          var missing = unresolved(list);
          if (missing.length) {
            relabel();
            openSizes(missing[0], 'Now choose a size for ' + (missing[0].getAttribute('data-name') || 'the next item') + '.');
            return;
          }
          pending = false;
          var total = totalOf(list);
          relabel();
          if (total !== pendingTotal) {
            /* The number on the button just moved. Show the new one and let them press it —
               never charge a total that was never displayed. */
            say(hintEl, 'Total updated for the sizes you picked.');
            if (addAll && !addAll.hidden) { addAll.focus(); }
            return;
          }
          say(hintEl, '');
          post(list.map(lineFor), addAll);
          return;
        }

        relabel();
        post([lineFor(row)], summary);
      });
    });

    if (addAll) {
      addAll.addEventListener('click', function () {
        if (busy) { return; }
        var list = candidates();
        if (list.length < 2) { relabel(); return; }
        var missing = unresolved(list);
        if (missing.length) {
          pending = true;
          pendingTotal = totalOf(list);
          openSizes(missing[0], 'Choose a size for ' + (missing[0].getAttribute('data-name') || 'this item') + ' to add the set.');
          return;
        }
        pending = false;
        say(hintEl, '');
        post(list.map(lineFor), addAll);
      });
    }

    /* ---- the buy box owns the anchor row's variant ------------------------------------ */
    var anchor = rows.filter(function (row) { return row.hasAttribute('data-anchor'); })[0];
    if (anchor) {
      document.addEventListener('forge:variant-change', function (e) {
        var v = e.detail && e.detail.variant;
        if (!v || !e.detail.available) {
          anchor.removeAttribute('data-vid');
        } else {
          anchor.setAttribute('data-vid', v.id);
          anchor.setAttribute('data-price', v.price_raw);
          var priceEl = anchor.querySelector('[data-forge-row-price]');
          if (priceEl && v.price) { priceEl.textContent = v.price; }
          var selEl = anchor.querySelector('[data-forge-row-sel]');
          if (selEl && v.title) { selEl.textContent = v.title; }
        }
        relabel();
      });
    }

    cartListeners.push(relabel);
    relabel();
  }

  Array.prototype.forEach.call(sets, initSet);

  /* Dawn publishes every successful add — including the buy box's own Add to cart and the
     single-variant rows in these modules — so the buttons de-duplicate against them too. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () { refreshCart(); });
  }
  refreshCart();
})();
